import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, Heart, ExternalLink, Users, Cpu, Github, Award, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { getLikedIds, setLikedIds } from "../lib/likes";

const initials = (name) =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const youtubeId = (url) => {
  const m = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m?.[1] || null;
};

export default function ProjectDetailModal({ project, onClose }) {
  const [full, setFull] = useState(project);
  const [liked, setLiked] = useState(false);
  const { t, lang } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    setFull(project);
    setLiked(project ? getLikedIds().includes(project.id) : false);
    if (!project) return;
    const load = () => api.get(`/projects/${project.id}?lang=${lang}`).then((r) => {
      setFull(r.data);
      window.dispatchEvent(new CustomEvent("kmu:data-changed"));
    }).catch(() => {});
    // Views count for signed-in members only
    if (user) api.put(`/projects/${project.id}/view`).then(load).catch(load);
    else load();
  }, [project, lang]);

  const like = async () => {
    if (!full) return;
    if (!user) { toast.info(t.like_login); return; }
    try {
      const r = await api.post(`/projects/${full.id}/like`);
      setFull((f) => ({ ...f, likes: r.data.likes }));
      const ids = new Set(getLikedIds());
      if (r.data.liked) ids.add(full.id); else ids.delete(full.id);
      setLikedIds([...ids]);
      setLiked(r.data.liked);
      if (r.data.liked) toast.success(t.like_toast);
    } catch {
      toast.error(t.like_fail);
    }
  };

  const ytId = youtubeId(full?.video_url);

  return (
    <AnimatePresence>
      {full && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/50 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="project-detail-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl"
          >
            <div className="relative h-64 overflow-hidden sm:h-80">
              <img src={resolveImage(full.image_url)} alt={full.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/25 to-transparent" />
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                data-testid="project-detail-close"
                aria-label="Close"
                className="absolute right-4 top-4 z-10 cursor-pointer rounded-full border border-white/30 bg-white p-2.5 text-slate-800 shadow-lg transition-all duration-300 hover:scale-110 hover:bg-slate-900 hover:text-white"
              >
                <X size={17} />
              </button>
              <div className="absolute bottom-5 left-6 right-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#2563EB] px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white">{full.category_label || full.category}</span>
                  <span className="rounded-full border border-white/30 bg-white/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-700 backdrop-blur-md">{full.department_label || full.department}</span>
                </div>
                <h2 className="mt-3 font-display text-2xl font-bold leading-tight text-white sm:text-3xl" data-testid="project-detail-title">
                  {full.title}
                </h2>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              {full.award && (
                <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-300/70 bg-amber-50 px-4 py-3" data-testid="project-award-banner">
                  <Award size={18} className="shrink-0 text-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">{full.award}</span>
                </div>
              )}
              {full.tagline && <p className="font-serif-accent text-lg italic text-[#1E3A8A]">"{full.tagline}"</p>}
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base" data-testid="project-detail-description">
                {full.description}
              </p>

              {ytId && (
                <div className="mt-8" data-testid="project-video-embed">
                  <h4 className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t.video_label}</h4>
                  <div className="relative aspect-video overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                    <iframe
                      src={`https://www.youtube.com/embed/${ytId}`}
                      title={`${full.title} demo video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 h-full w-full"
                    />
                  </div>
                </div>
              )}

              <div className="mt-8 grid gap-8 sm:grid-cols-2">
                <div>
                  <h4 className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                    <Users size={13} /> {t.team_label}
                  </h4>
                  <div className="mt-4 space-y-2.5">
                    {(full.team_members || []).map((m, i) => {
                      const email = (full.team_emails || [])[i];
                      return (
                        <div key={m} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5" data-testid={`team-member-${i}`}>
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1E3A8A]/10 font-mono text-[11px] font-semibold text-[#1E3A8A]">
                            {initials(m)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-800">{m}</p>
                            {email && (
                              <a href={`mailto:${email}`} data-testid={`team-email-${i}`} className="flex items-center gap-1 truncate text-xs text-[#2563EB] hover:underline">
                                <Mail size={11} /> {email}
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <h4 className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                    <Cpu size={13} /> {t.tech_label}
                  </h4>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(full.tech_stack || []).map((tech) => (
                      <span key={tech} className="rounded-md border border-[#2563EB]/25 bg-[#2563EB]/5 px-2.5 py-1 font-mono text-[10px] text-[#1E3A8A]">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-6">
                <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400" data-testid="project-detail-views">
                  <Eye size={14} className="text-[#2563EB]" /> {full.views?.toLocaleString()} {t.views_label}
                </span>
                <button
                  onClick={like}
                  data-testid="project-like-button"
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                    liked
                      ? "border-rose-400 bg-rose-50 text-rose-500"
                      : "border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-500"
                  }`}
                >
                  <Heart size={13} fill={liked ? "currentColor" : "none"} /> {full.likes?.toLocaleString()}
                </button>
                <div className="ml-auto flex items-center gap-2.5">
                  {full.github_url && (
                    <a
                      href={full.github_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="project-github-link"
                      className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900"
                    >
                      <Github size={13} /> GitHub
                    </a>
                  )}
                  {full.demo_url && (
                    <a
                      href={full.demo_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="project-demo-link"
                      className="flex items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#2563EB]"
                    >
                      {t.demo_link} <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
